import { useEffect, useState } from 'react';
import { Logger } from '@nextgen-web-framework/core';
import {
  Drawer,
  VariantSelector,
  Button,
  ButtonStyles,
  ButtonWidth,
  useTranslation,
  TextLink,
  Theme,
  Checkbox,
  Modal,
  ButtonAlignment,
  TextAlign,
  useDeviceTypeMediaQuery,
} from '@nextgen-web-framework/all';
import { productApi, ProjectionType } from '@commerce-product-webcomponents/sdk';
import {
  DynamicObjectInterface,
  CartCmsDataType,
  CMS_CONTENT_TYPE,
  StartKitDetailType,
} from '@commerce-profile-webcomponents/sdk';
import HtmlContent from '../HtmlContent/HtmlContent';
import logObject from '../../../../logObject';
import { parseString } from '../../../utils/helper';
import { fetchCmsContent } from '../../../utils/CMSUtils';
import ButtonWithLoading from '../ButtonWithLoading/ButtonWithLoading';
import ProductItem from '../ProductItem/ProductItem';
import { useGlobalErrorHandler } from '../../../utils/globalErrorHook';
import { ProductSelectionDrawerProps } from './types/propTypes';
import styles from './ProductSelectionDrawer.module.scss';

const ProductSelectionDrawer = ({
  title,
  productTitle,
  onClose,
  canClose,
  onProductSelect,
  products,
  selectedSku,
  showCancelButton = true,
  alertMessage,
  tncChecked = false,
}: ProductSelectionDrawerProps) => {
  const logger = Logger.getInstance(logObject('ProductSelectionDrawer.tsx', 'ProductSelectionDrawer'));
  const { isMobile } = useDeviceTypeMediaQuery();

  const t = useTranslation();

  const [selectedSKU, setSelectedSKU] = useState<StartKitDetailType>();
  const [isTncChecked, setIsTncChecked] = useState<boolean>(tncChecked);
  const [enableButton, setEnableButton] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cartCMSData, setCartCMSData] = useState<CartCmsDataType>({});
  const apiErrorHandler = useGlobalErrorHandler();

  useEffect(() => {
    getSKUName();
    fetchCartTermsAndConditions();
  }, []);

  useEffect(() => {
    const selectedItem = products.find((product: StartKitDetailType) => product.sku === selectedSku);
    setSelectedSKU(selectedItem);
  }, [selectedSku]);

  useEffect(() => {
    // Enable button if TnC is checked and selectedSKU.tncEnabled is true
    setEnableButton((isTncChecked && selectedSKU?.tncEnabled) || !selectedSKU?.tncEnabled);
  }, [isTncChecked, selectedSKU]);

  const fetchCartTermsAndConditions = async () => {
    try {
      const data = (await fetchCmsContent(CMS_CONTENT_TYPE.CART_TERMS_CONDITIONS, apiErrorHandler)) as CartCmsDataType;
      setCartCMSData(data);
    } catch (error) {
      logger.error('Error fetching content:', error);
      throw error;
    }
  };
  const getSKUName = () =>
    // Call product api to get product name, if empty
    products.map(async (product: StartKitDetailType) => {
      if (!product.productName) {
        const idList = [product.sku];
        const res = await apiErrorHandler(() =>
          productApi.getProductByIds({
            idList,
            projections: ProjectionType.Detailed,
          }),
        );
        const [producItem] = res?.products ?? [];

        return {
          ...product,
          productName: producItem?.name,
        };
      }
      return null;
    });
  const handleSelectSKU = (item: StartKitDetailType) => {
    setSelectedSKU(item);
  };
  const handleConfirmSKU = () => {
    setIsLoading(true);
    onProductSelect(selectedSKU?.sku ?? '');
  };
  const handleTncCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTncChecked((prev) => !prev);
  };
  const handleOpenModal = () => {
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
  };
  const handleDoneButton = () => {
    setIsTncChecked(true);
    setModalOpen(false);
  };

  const selectedItemText = selectedSKU?.selectedTitle ?? `${productTitle} ${selectedSKU?.productName}`;
  const tncText = t(`profile.AccountRenewal.selectKit.tnc${selectedSKU?.isUpgrade ? '.upgrade' : ''}`);
  const tncLinkText = t(`profile.AccountRenewal.selectKit.tncText`);
  return (
    <>
      <Drawer
        header={title}
        bottomGap={0}
        closeOnBackdropClick={showCancelButton}
        drawerWidth={!isMobile ? '430px' : '100%'}
        footer={
          <div className={styles['button-group']} data-testid="button-group">
            {showCancelButton && (
              <Button
                title={t('profile.AccountRenewal.cancelButton')}
                buttonStyle={ButtonStyles.OUTLINE}
                onClick={onClose}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                data-testid="cancel-button"
                disabled={isLoading}
              />
            )}
            <ButtonWithLoading
              title={t('profile.common.nextButton')}
              buttonStyle={ButtonStyles.SOLID}
              onClick={handleConfirmSKU}
              buttonWidth={ButtonWidth.FULL_WIDTH}
              disabled={!enableButton}
              data-testid="next-button"
              isLoading={isLoading}
            />
          </div>
        }
        onClose={onClose}
        closeIcon={canClose}
        subHeader={alertMessage}
      >
        <div className={styles['product-selection-container']} data-testid="product-selection-container">
          <ProductItem
            productName={selectedItemText}
            productDescription={selectedSKU?.productDescription}
            productQuantity={1}
            className={styles['product-item']}
          />

          <div className={styles['product-list']}>
            <div className={styles['type']}>
              {t('profile.AccountRenewal.selectKit.selectedType')}{' '}
              <span className={styles['selected']}>{selectedSKU?.productName}</span>
            </div>
            <div className={styles['list']}>
              {products.map((product: DynamicObjectInterface) => (
                <VariantSelector
                  key={product.sku}
                  id={product.sku}
                  className={styles['variant-selector']}
                  onVariantSelectorClick={() => handleSelectSKU(product)}
                  selected={product.sku === selectedSKU?.sku}
                  title={product.productName}
                />
              ))}
            </div>
          </div>
          {selectedSKU?.tncEnabled && (
            <div className={styles['tnc']}>
              <Checkbox
                className={styles['tnc-checkbox']}
                onChange={handleTncCheck}
                checked={isTncChecked}
                data-testid="tnc-checkbox"
                text={
                  <>
                    <div className={styles['tnc-text']}>
                      {tncText} <TextLink text={tncLinkText} theme={Theme.PURPLE} onClick={handleOpenModal} />
                    </div>
                  </>
                }
              />
            </div>
          )}
        </div>
      </Drawer>
      {modalOpen && (
        <div className={styles['modal-container']}>
          <div className={styles['modal-backdrop']}></div>
          <Modal
            headline={parseString(cartCMSData?.kit_terms_and_conditions?.title)}
            textAlign={TextAlign.CENTER}
            primaryButton={
              <Button
                title={t('profile.AccountRenewal.doneButton')}
                buttonStyle={ButtonStyles.SOLID}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                onClick={handleDoneButton}
              />
            }
            primaryButtonLabel={''}
            onClose={handleCloseModal}
            buttonAlignment={ButtonAlignment.VERTICAL}
            closeOnBackdropClick={true}
          >
            <div className={styles['modal-content']}>
              <HtmlContent content={parseString(cartCMSData?.kit_terms_and_conditions?.description)} />
            </div>
          </Modal>
        </div>
      )}
    </>
  );
};

export default ProductSelectionDrawer;
